import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
// Dev-only TanStack devtools panel. Lazy-loaded behind import.meta.env.DEV so
// the ~80 KB devtools bundle (lots of color, codecs, prism, etc.) is fully
// tree-shaken from production builds where the panel is never rendered.
const ReactQueryDevtools = import.meta.env.DEV
  ? React.lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })),
    )
  : null
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { ApiProvider } from '@/hooks/useAppApi'
import { NotificationProviderWrapper } from '@/providers/NotificationProviderWrapper'
import { LoginPage } from '@/pages/LoginPage'
import { UnauthorizedPage } from '@/pages/UnauthorizedPage'
import { WelcomePage } from '@/pages/WelcomePage'
import { AuthCallbackPage } from '@/platform/auth/AuthCallbackPage'
import { AppLayout } from '@/components/AppLayout'
import { getFeatureRoutes } from '@/features/registry'
import { SettingsPage } from '@/features/profile/pages/SettingsPage'
import { RegistrationStartPage } from '@/features/registration/pages/RegistrationStartPage'
import { RegistrationVerifyPage } from '@/features/registration/pages/RegistrationVerifyPage'
import { RegistrationCompletePage } from '@/features/registration/pages/RegistrationCompletePage'
import { OnboardingWizardPage } from '@/features/registration/pages/OnboardingWizardPage'
import { PublicProductApiDocsPage } from '@/pages/PublicProductApiDocsPage'
import { PendoRoot } from '@/platform/analytics/PendoRoot'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      retry: 1,
    },
  },
})

/**
 * Layout route that wraps authenticated application pages in the full AppShell
 * chrome (header + sidebar + status bar). Public/auth pages are intentionally
 * NOT rendered through this — they bring their own AuthShell.
 */
const AppLayoutRoute = (): JSX.Element => (
  <AppLayout>
    <Outlet />
  </AppLayout>
)

export const App = (): JSX.Element => {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider>
        <NotificationProviderWrapper>
          <BrowserRouter>
            {/* Product analytics (@revalize/pendo). Wraps the tree so the Pendo
                context is available to `usePendo()`; the agent itself loads 2s
                after auth resolves and self-disables when the environment has
                no Pendo config. Inside BrowserRouter so Pendo's own page
                tracking sees route changes. */}
            <PendoRoot>
            <Routes>
                {/* ── Public / auth routes — self-contained AuthShell chrome,
                       rendered OUTSIDE AppLayout so they never stack a second
                       (app) header on top of their own. ───────────────────── */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/register" element={<RegistrationStartPage />} />
                <Route path="/register/verify" element={<RegistrationVerifyPage />} />
                <Route path="/register/complete" element={<RegistrationCompletePage />} />
                <Route path="/product-api-docs" element={<PublicProductApiDocsPage />} />

                {/* ── Application routes — wrapped in AppLayout (header +
                       sidebar + status bar). ─────────────────────────────── */}
                <Route element={<AppLayoutRoute />}>
                  {/* `/dashboard` is declared by the dashboard feature entry
                      (`features/dashboard/index.tsx`) so it loads as its own lazy
                      chunk. It used to be declared here with a STATIC import, which
                      put the page's whole graph in the entry chunk for every session —
                      including sessions that never opened it. Do not re-add it here:
                      two routes for one path is ambiguous, and the static form undoes
                      the split that TRD §4's bundle budget depends on. */}

                  {/* Personal settings (3-scope cascade: system → tenant → user) */}
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <div className="p-4 sm:p-6">
                          <SettingsPage />
                        </div>
                      </ProtectedRoute>
                    }
                  />

                  {/* Onboarding wizard (post-registration) */}
                  <Route
                    path="/onboarding"
                    element={
                      <ProtectedRoute>
                        <div className="p-4 sm:p-6">
                          <OnboardingWizardPage />
                        </div>
                      </ProtectedRoute>
                    }
                  />

                  {/* Feature routes (admin, profile, docs, dev testing) */}
                  {getFeatureRoutes()}

                  {/* Default route */}
                  <Route path="/" element={<WelcomePage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
            </Routes>
            </PendoRoot>
          </BrowserRouter>
        </NotificationProviderWrapper>
      </ApiProvider>
      {ReactQueryDevtools ? (
        <React.Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </React.Suspense>
      ) : null}
    </QueryClientProvider>
  )
}
